import { useState, useId, useMemo } from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { Label } from "~/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { cn } from "~/lib/utils";

export interface TagOption<T extends string = string> {
  value: T;
  label: string;
  description?: string;
}

interface TagMultiSelectProps<T extends string = string> {
  label?: string;
  placeholder?: string;
  secondaryPlaceholder?: string;
  selectedValues: T[];
  options: TagOption<T>[];
  onChange: (values: T[]) => void;
  disabled?: boolean;
  error?: string;
}

export function TagMultiSelect<T extends string = string>({
  label,
  placeholder = "Add tags...",
  secondaryPlaceholder = "Add more tags...",
  selectedValues,
  options,
  onChange,
  disabled = false,
  error,
}: TagMultiSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const inputId = useId();
  const labelId = useId();

  const filteredOptions = useMemo(
    () =>
      options.filter((option) => {
        if (!searchTerm) {
          return true;
        }
        const searchLower = searchTerm.toLowerCase();
        return (
          option.label.toLowerCase().includes(searchLower) ||
          option.description?.toLowerCase().includes(searchLower)
        );
      }),
    [options, searchTerm]
  );

  const handleToggle = (value: T) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
    setSearchTerm("");
  };

  const handleRemove = (value: T) => {
    onChange(selectedValues.filter((v) => v !== value));
  };

  return (
    <div className="flex flex-col gap-2">
      {label && <Label id={labelId}>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild disabled={disabled}>
          <button
            type="button"
            id={inputId}
            aria-labelledby={label ? labelId : undefined}
            className={cn(
              "h-9 w-full border rounded-md bg-white hover:bg-accent/50 transition-colors cursor-pointer shadow-xs flex items-center px-3 gap-1",
              error ? "border-destructive" : "border-input"
            )}
          >
            {selectedValues.map((value) => {
              const option = options.find((o) => o.value === value);
              return (
                <Badge
                  key={value}
                  variant="secondary"
                  className="flex items-center gap-1 px-2 py-1 text-xs shrink-0 cursor-default"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="font-normal">{option?.label || value}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    className="h-3.5 w-3.5 flex items-center justify-center hover:bg-gray-900 rounded-full cursor-pointer transition-colors group"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemove(value);
                      }
                    }}
                    aria-label={`Remove ${option?.label}`}
                  >
                    <X className="h-3 w-3 text-muted-foreground group-hover:text-white transition-colors" />
                  </span>
                </Badge>
              );
            })}
            <div className="ml-0.5">
              {selectedValues.length < options.length && (
                <span className="text-muted-foreground text-sm truncate">
                  {selectedValues.length === 0 ? placeholder : secondaryPlaceholder}
                </span>
              )}
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 ml-auto" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0 z-[1400]"
          align="start"
        >
          <Command>
            <CommandInput
              placeholder="Search tags..."
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
            <CommandList>
              {filteredOptions.length === 0 && <CommandEmpty>No tags found.</CommandEmpty>}
              <CommandGroup>
                {filteredOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => {
                        handleToggle(option.value);
                      }}
                      className="flex items-center gap-3 cursor-pointer hover:!bg-secondary hover:!text-gray-900 transition-colors"
                    >
                      <div className="flex items-center justify-center w-4 h-4 shrink-0">
                        {isSelected && <Check className="h-4 w-4" />}
                      </div>
                      <div className="flex flex-col flex-1">
                        <span className="font-normal">{option.label}</span>
                        {option.description && (
                          <span className="text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}
